import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Car, User, Calendar, Shield, CheckCircle, FileSignature, AlertCircle, Upload } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { DocumentUploader, AttachmentList } from '../components/FileAttachments';

const API = process.env.REACT_APP_BACKEND_URL;
const STATES = ['GA', 'AL', 'FL', 'NC', 'SC', 'TN', 'MS', 'KY', 'VA', 'TX', 'CA', 'NY', 'Other'];

const RentalRequest = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rentalId = searchParams.get('vehicle') || '';

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // request_id after success
  const [draftId] = useState(() => 'draft-' + Math.random().toString(36).slice(2, 10));
  const [docsKey, setDocsKey] = useState(0);

  const [form, setForm] = useState({
    renterName: '',
    renterAddress: '',
    renterPhone: '',
    renterEmail: '',
    licenseNumber: '',
    licenseState: '',
    dateOfBirth: '',
    pickupDate: '',
    pickupTime: '',
    returnDate: '',
    returnTime: '',
    insuranceCompany: '',
    insurancePolicy: '',
    hasLicense: false,
    hasInsurance: false,
    hasCreditCard: false,
    acceptTerms: false,
    acceptLiability: false,
    additionalNotes: '',
  });

  useEffect(() => {
    if (!rentalId) { setLoading(false); return; }
    fetch(`${API}/api/rentals/vehicle/${rentalId}`)
      .then(r => r.ok ? r.json() : { vehicle: null })
      .then(d => { setVehicle(d.vehicle || null); setLoading(false); })
      .catch(() => setLoading(false));
  }, [rentalId]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.hasLicense || !form.hasInsurance || !form.hasCreditCard) {
      toast({ title: 'Documentation Required', description: 'Please confirm you have a valid license, insurance, and credit card.', variant: 'destructive' });
      return;
    }
    if (!form.acceptTerms || !form.acceptLiability) {
      toast({ title: 'Agreement Required', description: 'You must accept the rental agreement and liability terms.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/rentals/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rental_id: rentalId,
          vehicle_name: vehicle?.vehicle || '',
          ...form,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      // Attach any uploaded documents to the real request id
      try {
        await fetch(`${API}/api/uploads/documents/rebind`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entity_type: 'rental_request', from_entity_id: draftId, to_entity_id: data.request_id }),
        });
      } catch {}
      setSubmitted(data.request_id);
    } catch (err) {
      toast({ title: 'Request Failed', description: err.message, variant: 'destructive' });
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-red-500 border-t-transparent rounded-full" /></div>;
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="rental-request-missing">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-black mb-3">Vehicle Not Available</h1>
          <p className="text-gray-500 mb-6">This rental vehicle could not be found.</p>
          <Button onClick={() => navigate('/rentals')} variant="outline">Browse Rentals</Button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="rental-request-success">
        <Card className="max-w-lg w-full p-8 text-center border-green-200">
          <CheckCircle className="h-14 w-14 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-black mb-2">Request Submitted</h1>
          <p className="text-sm text-gray-500 mb-1">Reference: <span className="font-mono font-semibold text-black">{submitted}</span></p>
          <p className="text-sm text-gray-600 mt-4 mb-6">
            Your request for the <span className="font-semibold">{vehicle.vehicle}</span> has been received. An ACE team member will review availability and contact you within <span className="font-semibold">1 business hour</span>. You will receive an SMS confirmation once your rental is approved.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/rentals')} className="bg-red-600 hover:bg-red-700">Back to Rentals</Button>
            <a href="tel:6788463009" className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-gray-300 text-sm font-semibold hover:bg-gray-50">
              Call (678) 846-3009
            </a>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16" data-testid="rental-request-page">
      <div className="bg-black text-white py-12">
        <div className="max-w-5xl mx-auto px-6">
          <button onClick={() => navigate(`/rentals/${rentalId}`)} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-4" data-testid="back-to-rental-btn">
            <ArrowLeft className="h-4 w-4" /> Back to {vehicle.vehicle}
          </button>
          <p className="text-[10px] font-bold text-red-500 tracking-[0.3em] uppercase mb-2">Rental Request — Requires Approval</p>
          <h1 className="text-3xl sm:text-4xl font-bold" data-testid="rental-request-title">Request the {vehicle.vehicle}</h1>
          <p className="text-gray-400 text-sm mt-2">Submit your request. ACE will review and confirm availability within 1 business hour.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-5xl mx-auto px-6 py-8 space-y-6">

        {/* Renter Info */}
        <Card className="p-6" data-testid="renter-info-card">
          <div className="flex items-center gap-2 mb-4"><User className="h-5 w-5 text-red-600" /><h2 className="text-lg font-bold text-black">Renter Information</h2></div>
          <div className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input value={form.renterName} onChange={e => update('renterName', e.target.value)} placeholder="John David Lawrence" required data-testid="input-name" />
            </div>
            <div>
              <Label>Address *</Label>
              <Input value={form.renterAddress} onChange={e => update('renterAddress', e.target.value)} placeholder="1234 Main St, Atlanta, GA 30301" required data-testid="input-address" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Phone *</Label><Input type="tel" value={form.renterPhone} onChange={e => update('renterPhone', e.target.value)} placeholder="(404) 555-1234" required data-testid="input-phone" /></div>
              <div><Label>Email *</Label><Input type="email" value={form.renterEmail} onChange={e => update('renterEmail', e.target.value)} placeholder="john@example.com" required data-testid="input-email" /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2"><Label>Driver's License # *</Label><Input value={form.licenseNumber} onChange={e => update('licenseNumber', e.target.value)} placeholder="123456789" required data-testid="input-license" /></div>
              <div>
                <Label>State *</Label>
                <select value={form.licenseState} onChange={e => update('licenseState', e.target.value)} required className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="select-state">
                  <option value="">—</option>
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div><Label>Date of Birth *</Label><Input type="date" value={form.dateOfBirth} onChange={e => update('dateOfBirth', e.target.value)} required data-testid="input-dob" /></div>
          </div>
        </Card>

        {/* Rental Period */}
        <Card className="p-6" data-testid="rental-period-card">
          <div className="flex items-center gap-2 mb-4"><Calendar className="h-5 w-5 text-red-600" /><h2 className="text-lg font-bold text-black">Rental Period</h2></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Pickup Date *</Label><Input type="date" value={form.pickupDate} onChange={e => update('pickupDate', e.target.value)} required data-testid="input-pickup-date" /></div>
            <div><Label>Pickup Time *</Label><Input type="time" value={form.pickupTime} onChange={e => update('pickupTime', e.target.value)} required data-testid="input-pickup-time" /></div>
            <div><Label>Return Date *</Label><Input type="date" value={form.returnDate} onChange={e => update('returnDate', e.target.value)} required data-testid="input-return-date" /></div>
            <div><Label>Return Time *</Label><Input type="time" value={form.returnTime} onChange={e => update('returnTime', e.target.value)} required data-testid="input-return-time" /></div>
          </div>
        </Card>

        {/* Insurance */}
        <Card className="p-6" data-testid="insurance-card">
          <div className="flex items-center gap-2 mb-4"><Shield className="h-5 w-5 text-red-600" /><h2 className="text-lg font-bold text-black">Insurance</h2></div>
          <p className="text-xs text-gray-500 mb-4">Minimum full coverage required ($100k/$300k liability + comprehensive). A copy of your declarations page will be requested at pickup.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Insurance Company *</Label><Input value={form.insuranceCompany} onChange={e => update('insuranceCompany', e.target.value)} placeholder="State Farm" required data-testid="input-insurance-company" /></div>
            <div><Label>Policy Number *</Label><Input value={form.insurancePolicy} onChange={e => update('insurancePolicy', e.target.value)} placeholder="123-456-789" required data-testid="input-insurance-policy" /></div>
          </div>
        </Card>

        {/* Required Documentation */}
        <Card className="p-6" data-testid="documentation-card">
          <h2 className="text-lg font-bold text-black mb-3">Required Documentation (confirm you have all three)</h2>
          <div className="space-y-3">
            {[
              { k: 'hasLicense', label: "Valid driver's license (not expired, no restrictions)" },
              { k: 'hasInsurance', label: 'Proof of auto insurance (min $100K/$300K liability)' },
              { k: 'hasCreditCard', label: 'Credit card for security deposit authorization (no debit)' },
            ].map(({ k, label }) => (
              <label key={k} className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={form[k]} onCheckedChange={v => update(k, !!v)} className="mt-1" data-testid={`check-${k}`} />
                <span className="text-sm text-gray-700">{label}</span>
              </label>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-gray-100" data-testid="rental-docs-upload">
            <div className="flex items-center gap-2 mb-1"><Upload className="h-4 w-4 text-red-600" /><h3 className="text-sm font-bold text-black">Upload Your Documents (speeds up approval)</h3></div>
            <p className="text-xs text-gray-500 mb-3">Attach a photo of your driver's license and your insurance declarations page — JPG, PNG or PDF, up to 15MB each.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              <DocumentUploader entityType="rental_request" entityId={draftId} label="Driver's License" uploadedBy={form.renterName || 'renter'} buttonText="Upload License" onUploaded={() => setDocsKey(k => k + 1)} />
              <DocumentUploader entityType="rental_request" entityId={draftId} label="Insurance Declarations" uploadedBy={form.renterName || 'renter'} buttonText="Upload Insurance" onUploaded={() => setDocsKey(k => k + 1)} />
            </div>
            <AttachmentList entityType="rental_request" entityId={draftId} refreshKey={docsKey} emptyText="No documents uploaded yet (optional — can also be provided at pickup)." />
          </div>
        </Card>

        {/* Terms */}
        <Card className="p-6 bg-amber-50 border-amber-200" data-testid="terms-card">
          <div className="flex items-start gap-3 mb-3">
            <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-sm text-amber-900 mb-2">Rental Agreement Terms</h3>
              <ul className="text-xs text-amber-900 space-y-1 list-disc list-inside">
                <li>Age 25+ required with valid U.S. driver's license</li>
                <li>Full renter liability for damage, theft, and traffic citations</li>
                <li>Prohibited uses (track, racing, off-road) void insurance and incur $2,500+ fees</li>
                <li>Any accident must be reported to ACE and police immediately</li>
                <li>Return with same fuel level — refueling fee $9/gal; cleaning fee $350 if required</li>
                <li>Late return: 1 hr grace, then $50/hr up to a full day</li>
                <li>Full agreement signed at pickup; this request is for approval only</li>
              </ul>
            </div>
          </div>
          <div className="space-y-2 pt-2 border-t border-amber-200">
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox checked={form.acceptTerms} onCheckedChange={v => update('acceptTerms', !!v)} className="mt-0.5" data-testid="check-accept-terms" />
              <span className="text-xs font-semibold text-amber-900">I have read and agree to the ACE Vehicle Rental Agreement terms.</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox checked={form.acceptLiability} onCheckedChange={v => update('acceptLiability', !!v)} className="mt-0.5" data-testid="check-accept-liability" />
              <span className="text-xs font-semibold text-amber-900">I accept full liability for the vehicle and agree to indemnify ACE from all claims.</span>
            </label>
          </div>
        </Card>

        {/* Notes */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-3"><FileSignature className="h-5 w-5 text-red-600" /><h2 className="text-lg font-bold text-black">Additional Notes (Optional)</h2></div>
          <Textarea value={form.additionalNotes} onChange={e => update('additionalNotes', e.target.value)} placeholder="Event details, special requests, photoshoot info, etc." rows={3} data-testid="input-notes" />
        </Card>

        {/* Submit */}
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-between bg-black text-white rounded-xl p-5">
          <div className="flex items-center gap-3">
            <Car className="h-6 w-6 text-red-500" />
            <div>
              <p className="text-xs text-gray-400">Requesting</p>
              <p className="font-bold">{vehicle.vehicle} · ${vehicle.retail_rate}/day</p>
            </div>
          </div>
          <Button type="submit" disabled={submitting} className="w-full sm:w-auto h-12 px-8 bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="submit-request-btn">
            {submitting ? 'Submitting…' : 'Submit Rental Request'}
          </Button>
        </div>
        <p className="text-[11px] text-gray-400 text-center">No charge will be made until ACE approves your request.</p>
      </form>
    </div>
  );
};

export default RentalRequest;
